import { useAskFilters } from './AskFiltersContext';
import { useT } from '../I18nContext';

/**
 * Language picker on the rail. Native <select> for simplicity -
 * native dropdown gives a11y + mobile UX for free.
 *
 * Default is empty string ('Auto' label) → server detects from
 * the user's question. Picking a specific language forces every
 * subsequent reply into that language.
 *
 * The list is intentionally synchronised with the widget's i18n
 * catalogue and the server-side TTS/STT voice maps (see
 * widget-ui/src/i18n.ts, audio_service.py, routes/widget_api.py,
 * routes/api_admin_config.py). Every entry here has end-to-end
 * support: chat (Gemini), STT (Google) and TTS (Google voice).
 *
 * Adding a language? Update ALL of:
 *   - widget-ui/src/i18n.ts (LangCode, LANGUAGE_NAMES, TRANSLATIONS)
 *   - static/js/kb-widget.js (TRANSLATIONS, LANGUAGE_NAMES)
 *   - routes/widget_api.py (chat allowlist + TTS dict)
 *   - routes/api_admin_config.py (_SUPPORTED_LANGUAGES tuple)
 *   - routes/public_api.py (TTS language_codes dict)
 *   - audio_service.py (voice_map + _STT_BCP47_BY_LANG)
 *   - SUPPORTED_LANGUAGE_CODES below
 *   - documentation/Locksmith_Widget_v2_Documentation.md
 *   - documentation/Knowledge_Base_Public_API_Documentation_v1_3.md
 */
const LANGUAGES: ReadonlyArray<{ value: string; label: string }> = [
  { value: '',   label: 'Auto' },
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'fr', label: 'Français' },
  { value: 'de', label: 'Deutsch' },
  { value: 'nl', label: 'Nederlands' },
  { value: 'it', label: 'Italiano' },
  { value: 'pt', label: 'Português' },
  { value: 'ja', label: '日本語' },
  { value: 'ko', label: '한국어' },
  { value: 'zh', label: '中文' },
  { value: 'ar', label: 'العربية' },
  { value: 'hi', label: 'हिन्दी' },
  { value: 'ru', label: 'Русский' },
  { value: 'pl', label: 'Polski' },
  { value: 'tr', label: 'Türkçe' },
  { value: 'sv', label: 'Svenska' },
  { value: 'th', label: 'ไทย' },
  { value: 'vi', label: 'Tiếng Việt' },
];

/** Source of truth for AskFiltersContext to validate persisted
 *  picks. Keep in sync with LANGUAGES above (Auto / '' is always
 *  valid and not listed here). */
export const SUPPORTED_LANGUAGE_CODES: ReadonlySet<string> = new Set(
  LANGUAGES.map((l) => l.value).filter((v) => v !== ''),
);

/** Migration table for stored picks from older versions of this
 *  picker. Add entries when you rename a code (the old → new). */
export const LANGUAGE_CODE_MIGRATIONS: Readonly<Record<string, string>> = {
  // The picker used to ship 'zh-cn' (BCP-47 region tag) while every
  // server-side allowlist keyed on 'zh'. Migrate so existing users
  // don't lose their Chinese pick, and so TTS actually works.
  'zh-cn': 'zh',
};

export function LanguagePicker() {
  const { language, setLanguage } = useAskFilters();
  const t = useT();
  return (
    <select
      className="rail-language-picker"
      value={language}
      onChange={(e) => setLanguage(e.target.value)}
      title={t('language_picker.label')}
      aria-label={t('language_picker.label')}
    >
      {LANGUAGES.map((l) => (
        <option key={l.value || 'auto'} value={l.value}>
          {/* Endonyms (English, Español, 日本語…) stay native by
              convention, only 'Auto' is a translatable label. */}
          {l.value === '' ? t('language_picker.auto') : l.label}
        </option>
      ))}
    </select>
  );
}
